// ingest.episode-assets task (§ serial). The front gives ONLY the body (scenes +
// dialogue/action, speakers by name) — no pre-extracted cast/props/look. Walking
// episodes IN ORDER with a running context that starts EMPTY, the model discovers
// entities, 造型 (looks), identity merges, and — per scene — who is
// present, where, and in which look. Output is a structured Delta via native
// tool-calling (SERIAL_DELTA_SCHEMA): `name` and `aliases` are separate fields, so
// a canonical name can never be mangled by a markdown `名 | 名` delimiter — the
// failure mode where a model that wrote `大骨头（Big Bones / Marcus Vale）` got
// fragmented into ghost entities. Tool mode → serial profile must call functions.

import { emptyDelta, type Delta } from "../../../domain/ingest/serial-context.js";
import { SERIAL_DELTA_SCHEMA } from "../schemas.js";
import type { LlmTaskExecutor } from "../../types.js";

export const EPISODE_ASSETS_SYSTEM = [
  "你是剧本资产/造型标注器。前段只给了正文（场景、台词、动作），**没有任何现成的人物/道具/造型清单**——资产与造型全部由你从正文里逐集发现、逐集维护。",
  "结合【已知设定】（到目前为止你已建立的登记）与【本段正文】，调用 `emit_delta` 工具返回一个结构化对象覆盖这一整段的所有决策。空数组就传 `[]`。",
  "**【本段正文】可能包含若干集**（每集以 `# ep_NNN` 开头）；把它们当作连续推进的一段整体处理，跨集沿用同一 canonical 名字。",
  "始终使用【已知设定】里的既有 canonical 名字；同一个人全剧保持同一名字。",
  "人物 canonical 名必须采用【本段正文】scene.actors 或 speaker 中出现的原文姓名拼写；外语姓名保持外语原名，不翻译、不音译、不改成中文名。称谓、简称和译名只放 aliases 或 merges，不能取代已经出现的原文全名。",
  "",
  "**newEntities —— 本段【已知设定】里还没有的实体，每个登记一条**：",
  "  · name = 纯 canonical 名，**绝不含括号/斜杠/译名夹注/对白标记**（不要 name=『Marcus Vale（马库斯·维尔）』或『Marcus Vale，魂语』，而是 name=『Marcus Vale』）。人名采用正文里出现的原文完整姓名拼写（`Emily Cole` 不得改成 `艾米丽·科尔`；『苏若雪』不缩成『若雪』）。",
  "  · aliases = 这段里该实体的**所有其他叫法**，逐个单列（纯名，别带『（心）』『：』『，魂语』这类对白/情绪标记）：别名、译名、前世名、短名/全名、英文原名，**尤其对白说话人在正文里的写法**。正文说话人写『伊文·布鲁克斯』而你 canonical 是『大牛』→ 把『伊文·布鲁克斯』放进 aliases；写『大骨』而 canonical 是『大骨头』→ aliases 放『大骨』。系统靠 aliases 把正文里的说话人/称呼归到你这个实体，**aliases 漏了就会新造重复鬼影**。",
  "  · **同一个实体只登记一条，绝不拆成多条**。**不同的角色绝不互相列为别名**——哪怕关系密切、常同框：大牛 和 丹尼·克罗斯 是两个人，别把丹尼的名字写进大牛的 aliases，反之亦然。判据：aliases 只放**同一个人/物**的不同叫法，不放另一个角色。",
  "  · kind=actor/location/prop。",
  "  · description=一句话说明**这个实体本身是谁 / 什么身份 / 什么东西**，非空。人物只写身份、角色、关系，不写服装造型；绝不写它所处的场景 / 环境 / 地点 / 当时在发生什么。原文有身份信息就照抄 / 概括；原文没写就据台词 / 称呼 / 戏份补一句最简身份（如『苏家中间人』『急诊护士』『赵长河的手下』）。场景写空间用途；道具写物件用途。",
  "  · defaultLook=实体首次登记时必填的**默认造型**：name 固定写『常态』；description 写可直接用于视觉复现的外貌、服饰、材质、颜色、布景或形制，非空。人物身份与关系不能拿来充当造型描述。优先采用原文明确描写；原文信息不足时，结合实体身份与同场视觉信息补成最简、稳定、可画的常态造型。",
  "  · **登记范围**：人物——登记每个能独立说话、拿取或交接道具、推动动作的具体单人角色；众人、群众、路人、轿夫4名等无法区分成员的群体不登记为 actor，群体共同发声由正文的 group speaker 承载。地点——每个可独立布置和重复引用的稳定物理空间各登记一次；客厅、卧室、卫生间、大厅、广场、舞台、后台分别登记，相关空间用『豪宅·客厅』『豪宅·卧室』表达归属。地点其它写法用 merges 归一。道具——**只登记关键道具**：①剧情关键（信物/线索/推动情节，如第一集丢了第三集寻回的手机）或 ②跨≥2场需稳定复现、要美术保持一致的物件；一次性/纯功能/视觉无需追踪的（随手打电话的手机、背景杯子/路人车）不登记。",
  "  · 已登记过的实体不要重复登记；若它这段有了新叫法，用 merges 补，不用在 newEntities 再列一遍。",
  "**merges —— 本段冒出一个称呼，其实是某个【已知设定】里已登记实体的别名（尚未登记过这个称呼）**：{from: 本段这个称呼, into: 已登记实体的 canonical}。判据：(1) 字面变体（大骨=大骨头=大骨（BigBull）、苏若雪=若雪）；(2) 剧情推断同一（上下文/外观/关系/连续动作，如前文『小骨』本段点名『杰克·莱德』）。**只有当 from 确实是 into 的另一叫法时才 merge；名字或外观相似但是不同角色（如同为骷髅但身份不同）绝不 merge。** from 已经是独立登记的实体时，不要 merge（系统不会合并两个已登记实体）。",
  "  · **地点同样要 merge——判据一样：是不是同一个场**。同一个地方的不同写法要合成一条：①字面变体（『车轮旁』『矿车车轮旁』『矿车车轮』是同一处；带不带平台/区域前缀如『Dawnfall Online - 棺木铺』=『棺木铺』）；②剧情确定同一处（同一场戏在同一空间发生）。**不是同一个场就各自独立**（矿轨修造铺 和 棺木铺 是两个地方，不合）。别因为写法带了长前缀就当成新地点。",
  "**正文里每个和已登记 canonical 写法不同的名字，都要用 merges 归过去**——系统会照 merges + aliases 把正文（对白说话人、旁白文本）里的这些写法直接改写成 canonical。**漏掉某个写法，它就会变成一个游离的重复实体。** 所以本段正文里出现的每个人名/称呼，要么本身就是某 canonical，要么在 aliases/merges 里登记过。",
  "looks：默认造型之外、需要单独复现的新增造型。entity=实体 canonical 名；name=造型名；aliases=同义造型名；description=这套外观一句话。",
  "  **复用优先——造型默认从【已知设定】里该实体已列出的造型清单里选一个，逐字照抄那个已有造型名**。除非本段外观与所有已有造型都有实质差异（换了整套衣服/场景被毁/道具明显改形），才新开一条造型。不要因为本段原文用词不同就另造近义新名——已知设定里已有『黑色连帽卫衣』，本段又见黑色卫衣，照抄『黑色连帽卫衣』即可；同义换名只会把同一套衣服拆成多张图。",
  "  默认造型只写在 newEntities.defaultLook，不要在 looks 里重复；looks 只登记换装、变身、场景被毁、道具破损/发光等额外造型。人物=整体扮相；场景=环境外观；道具=物件外观。",
  "  造型名=需要美术单独画一张图的外观档位（常态、婚服、西装、龙形；场景的 白天/夜晚/被毁；道具的 破损/发光）。**造型名必须用中文**：正文里的英文专名（套装/职业/形态名，如 Sean's Revenge、Flamecaster、Grave Priest、Shieldguard）一律译成中文再作造型名（肖恩的复仇套装、焰术师、墓祭司、盾卫）。",
  "  description=这套外观长什么样的一句话（优先用原文描写：服饰/颜色/发型/配饰，或场景布景/道具形制）。**常态 description 不能留空。绝不写心情/身体状况/胖瘦**（满脸泪痕、愤怒、虚弱濒死、受伤、昏迷、消瘦——这些不是造型；判据：换成它，美术要不要另画一张图）。",
  "**lookChanges —— 造型变化（稀疏，只标变化处）**：某个实体的造型从某一场起变了，就标一条 {scene, entity, look}。scene=变化发生的场景id（scn_012）；entity=canonical 名；look=从这场起穿的造型（必须是你在 looks 里注册过的档位名）。",
  "  · **只标造型真的改变的那一场**（换装/变身/受损/回归日常）。例：大骨头在 scn_050 变身焰术师 → {scene:scn_050, entity:大骨头, look:焰术师态}；后面若换回常态，在那一场再标 {..., look:常态}。",
  "  · 没变化的场**不用标**——系统自动沿用上一次的造型一直到下一次变化。首次出现默认常态，也不用特意标。",
  "  · 你**不需要**列出每一场谁在场——谁在场系统直接从正文（对白说话人、旁白）读，你只管注册实体、归一名字、标造型变化。",
  "subEvents：本段关键剧情原子，每条一句话（维护剧情理解与压缩）。",
  "summary：本段一句话梗概；若本段含多集，概括为整段一句话。",
].join("\n");

export function buildEpisodeAssetsUser(contextMd: string, bodyMd: string): string {
  return [
    "【已知设定】（截至上一段你已建立的登记；开篇时为空）",
    contextMd.trim() || "（空，从这一段开始建立）",
    "",
    "【本段正文】（可能含多集，每集以 `# ep_NNN` 开头）",
    bodyMd.trim(),
  ].join("\n");
}

export async function runEpisodeAssets(executor: LlmTaskExecutor, contextMd: string, bodyMd: string, signal?: AbortSignal): Promise<{ delta: Delta; raw: string }> {
  const response = await executor.generateTool("ingest.episode-assets", {
    system: EPISODE_ASSETS_SYSTEM,
    user: buildEpisodeAssetsUser(contextMd, bodyMd),
    tool: { name: "emit_delta", description: "返回本段的资产/造型/场景 Delta", inputSchema: SERIAL_DELTA_SCHEMA },
    ...(signal ? { signal } : {}),
  });
  return { delta: toDelta(response.input), raw: JSON.stringify(response.input) };
}

// Schema-validated tool input → Delta. The schema guarantees shape, so this is a
// straight, defensive projection: entity `aliases` become immediate merges into
// the canonical name (so translations/past-life/alt names fold in), and look
// name+aliases become the `names[]` list (names[0]=canonical) applyDelta expects.
//
// Models (and some tool bridges) still occasionally emit array fields as JSON
// *strings* (double-encoded), sometimes with unescaped quotes inside. Historically
// `arr()` treated any non-array as `[]` and serial silently dropped every entity /
// look for that window. We now re-parse stringified arrays and **throw** on shape
// failure so the window can retry instead of shipping an empty registry.
const KIND = new Set(["actor", "location", "prop"]);
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

function requiredString(value: unknown, field: string): string {
  const parsed = str(value);
  if (!parsed) throw new SerialDeltaShapeError(`${field}: expected non-empty string`);
  return parsed;
}

function parseDefaultLook(value: unknown, field: string): { name: string; description: string } {
  if (!isObj(value)) throw new SerialDeltaShapeError(`${field}: expected object`);
  const name = requiredString(value.name, `${field}.name`);
  if (name !== "常态") throw new SerialDeltaShapeError(`${field}.name: expected 常态`);
  return { name, description: requiredString(value.description, `${field}.description`) };
}

/** Thrown when a serial delta field is not a usable array (incl. failed re-parse). */
export class SerialDeltaShapeError extends Error {
  readonly code = "serial_delta_shape" as const;
  constructor(message: string) {
    super(message);
    this.name = "SerialDeltaShapeError";
  }
}

/**
 * Best-effort repair when the model double-encodes an array as a JSON string and
 * uses bare `"` as Chinese quotation marks inside values
 * (e.g. `市中心"金鳞"商业地皮`, `皇冠"世纪之星"`). Strict JSON.parse fails; we
 * walk the text as JSON-ish strings and escape interior `"` that cannot close a
 * value (next non-ws is not `,` `}` `]` `:` or EOF). Only applied after a strict
 * parse fails — callers must not use this on already-valid JSON.
 */
export function repairDoubleEncodedJson(raw: string): string {
  let out = "";
  let inString = false;
  for (let i = 0; i < raw.length; i++) {
    const c = raw[i]!;
    if (!inString) {
      out += c;
      if (c === '"') inString = true;
      continue;
    }
    if (c === "\\" && i + 1 < raw.length) {
      out += c + raw[i + 1];
      i++;
      continue;
    }
    if (c === '"') {
      let j = i + 1;
      while (j < raw.length && /\s/.test(raw[j]!)) j++;
      const next = j < raw.length ? raw[j]! : "";
      // Structural close of a JSON string (key or value).
      if (next === "" || next === "," || next === "}" || next === "]" || next === ":") {
        out += c;
        inString = false;
      } else {
        // Interior bare quote (Chinese quotation marks etc.) → escape.
        out += '\\"';
      }
      continue;
    }
    out += c;
  }
  return out;
}

function parseJsonArrayString(t: string, field: string): unknown[] {
  const tryParse = (s: string): unknown => JSON.parse(s);
  let parsed: unknown;
  try {
    parsed = tryParse(t);
  } catch (first) {
    const repaired = repairDoubleEncodedJson(t);
    if (repaired === t) {
      throw new SerialDeltaShapeError(
        `${field}: expected JSON array string, parse failed: ${first instanceof Error ? first.message : String(first)}`,
      );
    }
    try {
      parsed = tryParse(repaired);
    } catch (second) {
      throw new SerialDeltaShapeError(
        `${field}: expected JSON array string, parse failed after quote-repair: ${second instanceof Error ? second.message : String(second)}`,
      );
    }
  }
  if (!Array.isArray(parsed)) {
    throw new SerialDeltaShapeError(`${field}: expected JSON array string, got ${typeof parsed}`);
  }
  return parsed;
}

/**
 * Coerce a tool field to an array. Accepts real arrays, null/undefined/"" → [],
 * and JSON-encoded array strings (double-encoding, with quote-repair). Anything
 * else throws — never silently collapses to [].
 */
export function coerceJsonArray(v: unknown, field: string): unknown[] {
  if (v == null) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    const t = v.trim();
    if (!t) return [];
    return parseJsonArrayString(t, field);
  }
  throw new SerialDeltaShapeError(`${field}: expected array, got ${typeof v}`);
}

// Deterministic backstop for the one thing the model still slips on: a canonical
// name carrying an inline paren annotation — `大骨头（Big Bones / Marcus Vale）`,
// `银蕾丝(SilverLace)`. The schema asks for name/aliases split, but a few leak
// through, so we split mechanically here (100% reliable, model-independent): the
// text before the first paren is the clean name; the paren's inner content, split
// on / , 、; separators, is preserved as aliases (never discarded — an inline
// annotation is the source explicitly declaring the alias mapping). Tolerates an
// unclosed paren (`大骨（Big Bones`). A leading paren (no name before it) is left
// whole — it isn't a name annotation.
const PAREN_RE = /[（(]([^）)]*)[）)]?/;
function splitParen(raw: string): { name: string; aliases: string[] } {
  const s = str(raw);
  const m = PAREN_RE.exec(s);
  if (!m) return { name: s, aliases: [] };
  const name = s.slice(0, m.index).trim();
  if (!name) return { name: s, aliases: [] };
  const aliases = (m[1] ?? "").split(/[/／,，、;；]/).map((x) => x.trim()).filter(Boolean);
  return { name, aliases };
}
const cleanName = (raw: unknown): string => splitParen(str(raw)).name;

function asDeltaObject(input: unknown): Record<string, unknown> | null {
  if (isObj(input)) return input;
  if (typeof input === "string") {
    const t = input.trim();
    if (!t) return null;
    let parsed: unknown;
    try {
      parsed = JSON.parse(t);
    } catch (e) {
      throw new SerialDeltaShapeError(
        `delta: top-level JSON string failed to parse: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
    if (!isObj(parsed)) {
      throw new SerialDeltaShapeError(`delta: top-level JSON string must be an object, got ${typeof parsed}`);
    }
    return parsed;
  }
  return null;
}

export function toDelta(input: unknown): Delta {
  const d = emptyDelta();
  const obj = asDeltaObject(input);
  if (!obj) return d;
  for (const e of coerceJsonArray(obj.newEntities, "newEntities")) {
    if (!isObj(e)) continue;
    const kind = str(e.kind);
    const { name, aliases: parenAliases } = splitParen(str(e.name));
    if (!KIND.has(kind) || !name) continue;
    const description = requiredString(e.description, `newEntities.${name}.description`);
    const defaultLook = parseDefaultLook(e.defaultLook, `newEntities.${name}.defaultLook`);
    // Every 叫法 → clean alias. A declared alias may ITSELF carry a paren
    // (`旧药箱（带三角划痕）`) — split it, else the polluted form registers as an
    // alias and its extra length promotes it to canonical (fuller-name rule).
    const aliasNames = [...parenAliases];
    for (const a of coerceJsonArray(e.aliases, "newEntities.aliases")) {
      const { name: base, aliases: inner } = splitParen(str(a));
      if (base) aliasNames.push(base);
      aliasNames.push(...inner);
    }
    const aliases = [...new Set(aliasNames.filter((a) => a && a !== name))];
    d.newEntities.push({ name, kind: kind as "actor" | "location" | "prop", description, aliases, defaultLook });
  }
  for (const m of coerceJsonArray(obj.merges, "merges")) {
    if (!isObj(m)) continue;
    const from = cleanName(m.from);
    const into = cleanName(m.into);
    if (from && into && from !== into) d.merges.push({ from, into });
  }
  for (const l of coerceJsonArray(obj.looks, "looks")) {
    if (!isObj(l)) continue;
    const entity = cleanName(l.entity);
    const name = cleanName(l.name);
    if (!entity || !name) continue;
    const names = [name, ...coerceJsonArray(l.aliases, "looks.aliases").map(cleanName).filter(Boolean)];
    d.looks.push({ entity, names, description: requiredString(l.description, `looks.${entity}.${name}.description`) });
  }
  for (const c of coerceJsonArray(obj.lookChanges, "lookChanges")) {
    if (!isObj(c)) continue;
    const scene = str(c.scene);
    const entity = cleanName(c.entity);
    const look = cleanName(c.look);
    if (scene && entity && look) d.lookChanges.push({ scene, entity, look });
  }
  d.subEvents = coerceJsonArray(obj.subEvents, "subEvents").map(str).filter(Boolean);
  d.summary = str(obj.summary);
  return d;
}
